import { useEffect, useState, type MouseEvent } from 'react'
import { Milestone, AlertTriangle, Building2, Plus, Presentation, Search } from 'lucide-react'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatDate } from '@/lib/dates'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { EmptyState } from '@/components/ui/empty-state'
import { ListSkeleton } from '@/components/ui/skeleton'
import { useTrackerCompanies, useTrackerTickets } from '@/queries/tracker'
import { useIsAdmin } from '@/lib/auth'
import { formatTicketRef } from '@/lib/ticketRef'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { CreateTrackedItemDialog } from './CreateTrackedItemDialog'
import { WaitingOnPill } from './WaitingOnPill'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  devStageLabels,
  devStageVariants,
  priorityLabels,
} from '@/lib/status'
import type { TrackerStatus, TrackerTicket, TrackerWaitingOn } from '@/types/tracker'
import type { TicketPriority } from '@/types/ticket'

const TRACKER_STATUSES: TrackerStatus[] = [
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]

const PRIORITIES: TicketPriority[] = ['high', 'normal', 'low']

type PresentSearch = { status?: TrackerStatus; priority?: TicketPriority; q?: string; waitingOn?: TrackerWaitingOn }

// V2 filterbar select (mockup `.filterbar select`): pill radius, 36px,
// hairline border — shared by every select in this filterbar.
const filterSelectClass =
  'h-[var(--etn-control-height-input)] shrink-0 rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

// V2 table header cell (mockup `th`): 36px, 11px uppercase letterspaced label.
const thClass =
  'h-9 whitespace-nowrap border-b border-[var(--etn-gray-100)] px-4 text-left text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label'

// V2 table card. overflow-x-auto rather than overflow-hidden: still clips to
// the rounded corners, but a narrow viewport scrolls a wide table instead of
// silently losing its right-hand columns.
const tableCardClass =
  'overflow-x-auto rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper'

function TrackerRow({
  ticket,
  asUser,
  presentSearch,
  showPresentAction,
}: {
  ticket: TrackerTicket
  asUser?: string
  /** The list's current filters, carried into the Present link — see TrackerListView. */
  presentSearch: PresentSearch
  /** Whether the CURRENT VIEWER is staff — independent of the list's own admin mode, since a staffer browsing their own /tracker still gets the affordance. Decided once by the list so every row in a render agrees. */
  showPresentAction: boolean
}) {
  const navigate = useNavigate()
  const { tracker } = ticket
  const to = `/tracker/${ticket._id}`
  const search = { asUser }

  const goToTicket = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.target instanceof Element && e.target.closest('a, button')) return
    navigate({ to: to as never, search: search as never })
  }

  return (
    <tr onClick={goToTicket} className="group cursor-pointer text-etn-cell transition-colors hover:bg-etn-row-hover">
      <td className="h-11 max-w-[380px] truncate px-4 align-middle font-medium text-etn-text">
        <Link to={to as never} search={search as never} className="hover:underline">
          {ticket.subject}
        </Link>
        <small className="ml-2 font-normal text-etn-micro text-etn-text-soft">{formatTicketRef(ticket)}</small>
      </td>
      <td className="h-11 px-4 align-middle">
        <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
      </td>
      <td className="h-11 px-4 align-middle">
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
      </td>
      <td className="h-11 max-w-[160px] truncate px-4 align-middle font-normal text-etn-text-soft">{tracker.category ?? ''}</td>
      <td className="h-11 max-w-[320px] px-4 align-middle">
        <div className="flex min-w-0 items-center gap-2">
          {tracker.nextAction ? (
            <span className="truncate font-normal text-etn-text-cell" title={tracker.nextAction}>
              {tracker.nextAction}
            </span>
          ) : (
            !tracker.waitingOn && <span className="font-normal text-etn-text-soft">—</span>
          )}
          <WaitingOnPill waitingOn={tracker.waitingOn} companyName={ticket.companyName} />
        </div>
      </td>
      <td className="h-11 whitespace-nowrap px-4 align-middle font-normal text-etn-text-soft">
        {tracker.expectedDate ? formatDate(tracker.expectedDate.slice(0, 10)) : '—'}
      </td>
      {showPresentAction && (
        <td className="h-11 px-4 align-middle text-right">
          <Link
            to="/tracker/present"
            search={{ companyId: ticket.companyId, asUser, ...presentSearch } as never}
            aria-label="Present"
            title="Present"
            className="inline-flex rounded-etn-menu p-1.5 text-etn-text-soft opacity-0 transition-opacity hover:bg-etn-control-subtle hover:text-etn-text group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Presentation className="h-4 w-4" />
          </Link>
        </td>
      )}
    </tr>
  )
}

export function TrackerListView({
  asUser,
  admin = false,
  companyId = '',
  onCompanyChange,
}: {
  asUser?: string
  admin?: boolean
  /** Staff's chosen company, kept in the route's URL. */
  companyId?: string
  onCompanyChange?: (companyId: string) => void
}) {
  const [status, setStatus] = useState<TrackerStatus | 'all'>('all')
  const [priority, setPriority] = useState<TicketPriority | 'all'>('all')
  const [waitingOn, setWaitingOn] = useState<TrackerWaitingOn | 'all'>('all')

  // Staff-only, like TrackerControls' "Track this issue" — decided once here
  // (not per row) so the Actions column header and every row's cell agree on
  // whether it exists. Independent of `admin` (the list's own cross-company
  // mode): a staffer browsing their own /tracker still gets the affordance.
  const isAdminViewer = useIsAdmin()
  // Staff see one company at a time and nothing until they pick one: the tracker is shared on screen with clients, so
  // it must never show another client's issues. The API refuses a staff list without a company too.
  const staffView = admin || (isAdminViewer && !asUser)
  const needsCompany = staffView && !companyId

  // Debounced search, same shape as AdminQueueView's: local input state, and
  // an applied value that drives both the request and the Present link so
  // the two never disagree about what's actually been searched for.
  const [searchText, setSearchText] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 300)
    return () => clearTimeout(t)
  }, [searchText])

  const params: Record<string, unknown> = {}
  if (status !== 'all') params.status = status
  if (priority !== 'all') params.priority = priority
  if (waitingOn !== 'all') params.waitingOn = waitingOn
  if (q) params.q = q
  if (staffView) {
    // Staff ask for the server's max page: an imported tracker can run past the 25-row default.
    params.limit = 100
    if (companyId) params.companyId = companyId
  }

  const { data, isLoading, error, refetch } = useTrackerTickets(params, asUser, { enabled: !needsCompany })
  const companies = useTrackerCompanies(staffView)

  const rows = data?.data ?? []
  // True once the server-side page itself is short of the real total.
  const truncated = staffView && (data?.total ?? 0) > rows.length

  // Carried into each row's Present link (design doc "Presenting follows the
  // list's filters", 2026-08-24) — built once here, not per-row, so every
  // row's deck link agrees on exactly what's currently applied to the list.
  const presentSearch: PresentSearch = {}
  if (status !== 'all') presentSearch.status = status
  if (priority !== 'all') presentSearch.priority = priority
  if (waitingOn !== 'all') presentSearch.waitingOn = waitingOn
  if (q) presentSearch.q = q

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex gap-8">
        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h1 className="text-lg font-semibold text-etn-text">Tracker</h1>
            {/* Not while lensed: the lens shows what the client sees, and they can't add items. */}
            {isAdminViewer && !asUser && (
              <CreateTrackedItemDialog
                trigger={
                  <Button size="sm">
                    <Plus className="h-4 w-4" />
                    New tracked item
                  </Button>
                }
              />
            )}
          </div>

          <div className="mb-5 flex flex-wrap items-center gap-2.5">
            {staffView && (
              <select
                aria-label="Company"
                className={`${filterSelectClass} max-w-56`}
                value={companyId}
                onChange={e => onCompanyChange?.(e.target.value)}
              >
                <option value="">Select a company…</option>
                {companies.data?.data.map(c => (
                  <option key={c._id} value={c._id}>{c.name ?? c._id}</option>
                ))}
              </select>
            )}
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-etn-text-soft" />
              <Input
                pill
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search subjects…"
                aria-label="Search tracked issues"
                className="pl-9"
              />
            </div>
            <select
              aria-label="Status"
              className={filterSelectClass}
              value={status}
              onChange={e => setStatus(e.target.value as TrackerStatus | 'all')}
            >
              <option value="all">All statuses</option>
              {TRACKER_STATUSES.map(s => (
                <option key={s} value={s}>{trackerStatusLabels[s]}</option>
              ))}
            </select>
            <select
              aria-label="Priority"
              className={filterSelectClass}
              value={priority}
              onChange={e => setPriority(e.target.value as TicketPriority | 'all')}
            >
              <option value="all">All priorities</option>
              {PRIORITIES.map(p => (
                <option key={p} value={p}>{priorityLabels[p]}</option>
              ))}
            </select>
            <select
              aria-label="Waiting on"
              className={filterSelectClass}
              value={waitingOn}
              onChange={e => setWaitingOn(e.target.value as TrackerWaitingOn | 'all')}
            >
              <option value="all">Waiting on: any</option>
              <option value="etainabl">Waiting on Etainabl</option>
              <option value="client">{isAdminViewer ? 'Waiting on the client' : 'Waiting on your team'}</option>
            </select>
            <span className="ml-auto flex items-center gap-1.5 text-etn-micro text-etn-text-muted">
              <span aria-hidden className="text-etn-accent-text-active">●</span> Live
            </span>
          </div>

          {needsCompany && companies.error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the companies."
                body="The company list didn't load, so there's nothing to pick yet. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => companies.refetch()}>Retry</Button>
            </div>
          )}

          {needsCompany && !companies.error && (
            <EmptyState
              icon={<Building2 />}
              title="Choose a company"
              body="The tracker shows one company at a time, so it's safe to share your screen with that client."
            />
          )}

          {isLoading && <ListSkeleton rows={5} />}

          {error && (
            <div className="flex flex-col items-center gap-3 py-16">
              <EmptyState
                icon={<AlertTriangle />}
                title="Couldn't load the tracker."
                body="Something went wrong. Please try again."
              />
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          )}

          {!needsCompany && !isLoading && !error && rows.length === 0 && (
            <EmptyState
              icon={<Milestone />}
              title="No promoted tickets yet"
              body="When we promote a ticket to the tracker it'll show up here."
            />
          )}

          {!isLoading && !error && rows.length > 0 && (
            <>
              {truncated && (
                <p className="mb-2 text-etn-micro text-etn-text-soft">
                  Showing first {rows.length} of {data!.total}. Narrow by status or search to see the rest.
                </p>
              )}
              <div className={tableCardClass}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" className={thClass}>Issue</th>
                      <th scope="col" className={thClass}>Status</th>
                      <th scope="col" className={thClass}>Progress</th>
                      <th scope="col" className={thClass}>Category</th>
                      <th scope="col" className={thClass}>Next action</th>
                      <th scope="col" className={thClass}>Expected</th>
                      {isAdminViewer && (
                        <th scope="col" className={thClass}><span className="sr-only">Actions</span></th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-etn-border-row">
                    {rows.map((t: TrackerTicket) => (
                      <TrackerRow
                        key={t._id}
                        ticket={t}
                        asUser={asUser}
                        presentSearch={presentSearch}
                        showPresentAction={isAdminViewer}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
